import React, {forwardRef, useCallback, useRef, useState} from "react";
import {createUseStyles} from "react-jss";
import {HorizontalAlign, VerticalAlign} from "../helpers/enums";
import {SET_ACTIVE_ELEMENT, useScrollytellDispatch, useScrollytellState,} from "../Admin/scrollytellContext";
import {EditStickyBlockContainer} from "./EditStickyBlockContainer";
import {resolveCardTextColor} from "./helpers";

const useStyles = createUseStyles((theme) => {
  const colors = theme?.colors || {};
  return {
    container: {
      backgroundColor: ({ backgroundColor, isMapCard }) =>
        backgroundColor || (isMapCard ? "rgba(15, 23, 42, 0.88)" : "transparent"),
      backdropFilter: ({ isMapCard }) => (isMapCard ? "blur(12px)" : "none"),
      WebkitBackdropFilter: ({ isMapCard }) => (isMapCard ? "blur(12px)" : "none"),
      boxShadow: ({ isMapCard }) => (isMapCard ? "0 8px 32px rgba(0, 0, 0, 0.35)" : "none"),
      color: ({ backgroundColor, isMapCard }) =>
        resolveCardTextColor(backgroundColor, isMapCard),
      borderRadius: ({ borderRadius, isMapCard }) => (borderRadius || isMapCard ? "8px" : "0px"),
      padding: "16px",
      display: "flex",
      flexDirection: "column",
      position: "relative",
      pointerEvents: "auto",
      height: ({ height }) => height,
      alignItems: ({ horizontalAlign }) => {
        if (horizontalAlign === HorizontalAlign.RIGHT) {
          return "flex-end";
        } else if (horizontalAlign === HorizontalAlign.CENTER) {
          return "center";
        } else {
          return "flex-start";
        }
      },
      justifyContent: ({ verticalAlign }) => {
        if (verticalAlign === VerticalAlign.BOTTOM) {
          return "flex-end";
        } else if (verticalAlign === VerticalAlign.CENTER) {
          return "center";
        } else if (verticalAlign === VerticalAlign.SPACE) {
          return "space-evenly";
        } else {
          return "flex-start";
        }
      },
      border: ({ isAdminMode, isActiveContainer }) =>
        isAdminMode
          ? isActiveContainer
            ? `2px solid ${colors.adminBlue || "#4484F1"}`
            : `2px dashed ${colors.adminGray || "#9E9E9E"}`
          : "",
      "&.hover": {
        border: ({ isAdminMode, isActiveContainer }) =>
          isAdminMode
            ? `2px ${isActiveContainer ? "solid" : "dashed"} ${colors.adminBlue || "#4484F1"}`
            : "",
      },
    },
    emptyContainer: {
      minHeight: ({ minHeight }) => minHeight,
    },
  blockBackground: {
    padding: "50px",
    backgroundColor: "white",
    color: "#333",
  },
  textBackground: {
    display: "inline",
    backgroundColor: "white",
    boxShadow: "0px 0px 0px 4px white",
    color: "#333",
  },
  noBackground: {
    fontSize: "48px",
    color: "white",
  },
};
});

export const StickyBlockContainer = forwardRef(
  (
    {
      sectionId,
      containerId,
      containersCount,
      isAdminMode,
      container,
      hasBlocks,
      isMapCard = false,
      isThumbnail = false,
      children,
    },
    forwardRef
  ) => {
    const {
      backgroundColor,
      startColumn = 5,
      endColumn = 6,
      horizontalAlign = HorizontalAlign.CENTER,
      verticalAlign = VerticalAlign.CENTER,
      stepPosition = VerticalAlign.CENTER,
      gridSize,
      borderRadius,
    } = container;

    const [hover, setHover] = useState(false);
    const dispatch = useScrollytellDispatch();
    const { activeElement } = useScrollytellState();

    const isActiveContainer =
      activeElement[0] === sectionId &&
      activeElement[1] === containerId;

    const classes = useStyles({
      stepPosition,
      startColumn,
      endColumn,
      horizontalAlign,
      verticalAlign,
      backgroundColor,
      isAdminMode,
      isActiveContainer,
      containersCount,
      borderRadius,
      isMapCard,
      minHeight: "3.5rem",
    });
    const ref = useRef(null);
    const setRefs = useCallback(
      (node) => {
        ref.current = node;
        if (typeof forwardRef === "function") {
          forwardRef(node);
        } else if (forwardRef && typeof forwardRef === "object") {
          forwardRef.current = node;
        }
      },
      [forwardRef]
    );

    const handleMouseOver = event => {
      event.stopPropagation();
      setHover(true);
    }

    const handleMouseOut = event => {
      event.stopPropagation();
      setHover(false);
    }

    const handleClick = event => {
      if (!isActiveContainer) {
        event.stopPropagation();
        dispatch({
          type: SET_ACTIVE_ELEMENT,
          activeSection: sectionId,
          activeContainer: containerId,
        });
      }
    };

    return (
      <div
        id={isThumbnail ? undefined : `container-${sectionId}-${containerId}`}
        className={`${classes.container} ${isActiveContainer ? "active" : ""} ${
          hover ? "hover" : ""
        } ${hasBlocks ? "" : classes.emptyContainer}`}
        style={{
          color: resolveCardTextColor(backgroundColor, isMapCard),
        }}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
        onClick={handleClick}
        ref={setRefs}
      >
        <EditStickyBlockContainer
            sectionId={sectionId}
            containerId={containerId}
            isAdminMode={isAdminMode}
            gridSize={gridSize}
            container={container}
        />
        {children}
      </div>
    );
  }
);
